import { all, takeLatest, put, call, select } from 'redux-saga/effects';
import { getOrganizations } from '../organizations/slice';
import { getInsuredRisks } from '../insuredRisks/slice';
import { getFileContainers } from '../fileContainers/slice';
import { getCustomerPortalAvailability } from '../stripe/slice';
import { getWorklistsItemCount } from '../worklists/slice';
import { onSetPassword } from '../password/api';
import { msalInstance } from '../../config/msal';
import {
  loginWithPassword,
  loginWithPasswordSucceeded,
  loginWithPasswordFailed,
  loginWithPasswordAndApproveTermsOfUse,
  loginWithPasswordAndApproveTermsOfUseSucceeded,
  loginWithPasswordAndApproveTermsOfUseFailed,
  loginWithMicrosoft,
  loginWithMicrosoftSucceeded,
  loginWithMicrosoftFailed,
  loginWithMicrosoftAndApproveTermsOfUse,
  loginWithMicrosoftAndApproveTermsOfUseSucceeded,
  loginWithMicrosoftAndApproveTermsOfUseFailed,
  refreshMicrosoftSession,
  refreshMicrosoftSessionSucceeded,
  refreshMicrosoftSessionFailed,
  validateUser,
  validateUserSucceeded,
  validateUserFailed,
  logout,
  logoutSucceeded,
  getAllowedSessionTypes,
  getAllowedSessionTypesSucceeded,
  getAllowedSessionTypesFailed,
  getUiRights,
  getUiRightsSucceeded,
  getUiRightsFailed,
  approveTermsOfUse,
  approveTermsOfUseSucceeded,
  approveTermsOfUseFailed
} from './slice';
import { selectUiRights } from './selectors';
import {
  onLoginWithPassword,
  onLoginWithMicrosoft,
  onRefreshMicrosoftSession,
  onLogout,
  onValidateUser,
  onGetAllowedSessionTypes,
  onGetUiRights,
  onApproveTermsOfUse
} from './api';

export function* loginWithPasswordWorker({ payload }) {
  const { username, password, callback } = payload;
  try {
    const response = yield call(onLoginWithPassword, username, password);
    const userData = response.data;

    yield all([put(loginWithPasswordSucceeded(userData)), put(getUiRights({ userId: userData.id }))]);
    callback?.();
  } catch {
    yield put(loginWithPasswordFailed());
  }
}

export function* loginWithPasswordAndApproveTermsOfUseWorker({ payload }) {
  const { token, username, password, navigate } = payload;
  try {
    yield call(onSetPassword, token, password);
    const loginResponse = yield call(onLoginWithPassword, username, password);
    const userData = loginResponse.data;

    yield call(onApproveTermsOfUse, userData.id);

    const updatedUserResponse = yield call(onValidateUser);
    const updatedUserData = updatedUserResponse.data;

    yield all([
      put(loginWithPasswordAndApproveTermsOfUseSucceeded(updatedUserData)),
      put(getUiRights({ userId: updatedUserData.id })),
      call(navigate, '/')
    ]);
  } catch {
    yield put(loginWithPasswordAndApproveTermsOfUseFailed());
  }
}

export function* loginWithMicrosoftWorker({ payload }) {
  const { accessToken } = payload;
  try {
    const response = yield call(onLoginWithMicrosoft, accessToken);
    const userData = response.data;

    yield all([put(loginWithMicrosoftSucceeded(userData)), put(getUiRights({ userId: userData.id }))]);
  } catch {
    yield put(loginWithMicrosoftFailed());
  }
}

export function* loginWithMicrosoftAndApproveTermsOfUseWorker({ payload }) {
  const { accessToken, navigate } = payload;
  try {
    const loginResponse = yield call(onLoginWithMicrosoft, accessToken);
    const userData = loginResponse.data;

    yield call(onApproveTermsOfUse, userData.id);

    yield all([
      put(loginWithMicrosoftAndApproveTermsOfUseSucceeded(userData)),
      put(getUiRights({ userId: userData.id })),
      call(navigate, '/')
    ]);
  } catch {
    yield put(loginWithMicrosoftAndApproveTermsOfUseFailed());
  }
}

export function* refreshMicrosoftSessionWorker({ payload }) {
  const { accessToken } = payload;
  try {
    const response = yield call(onRefreshMicrosoftSession, accessToken);
    const userData = response.data;

    yield put(refreshMicrosoftSessionSucceeded(userData));
  } catch {
    yield put(refreshMicrosoftSessionFailed());
  }
}

export function* validateUserWorker() {
  try {
    const response = yield call(onValidateUser);
    const userData = response.data;
    yield all([put(validateUserSucceeded(userData)), put(getUiRights({ userId: userData.id }))]);
  } catch {
    yield put(validateUserFailed());
  }
}

export function* getAllowedSessionTypesWorker({ payload }) {
  const { currentUserEmail } = payload;
  try {
    const response = yield call(onGetAllowedSessionTypes, currentUserEmail);
    yield put(getAllowedSessionTypesSucceeded(response.data));
  } catch {
    yield put(getAllowedSessionTypesFailed());
  }
}

export function* logoutWorker() {
  try {
    yield call(onLogout);
    msalInstance.clearCache();
    yield put(logoutSucceeded());
  } catch {
    msalInstance.clearCache();
    yield put(logoutSucceeded());
  }
}

export function* getUiRightsWorker({ payload }) {
  const { userId } = payload;
  try {
    const response = yield call(onGetUiRights, userId);
    yield put(getUiRightsSucceeded(response.data));
  } catch {
    yield put(getUiRightsFailed());
  }
}

export function* approveTermsOfUseWorker({ payload }) {
  const { userId } = payload;
  try {
    yield call(onApproveTermsOfUse, userId);
    yield put(approveTermsOfUseSucceeded());
    yield put(validateUser());
  } catch {
    yield put(approveTermsOfUseFailed());
  }
}

/* 
This saga function should load all environment specific data (like organizations, insured risks or housing stocks) 
that is used in different tabs and that never or seldom changes within an instance.
*/
export function* loadEnvironmentSpecificDataWorker() {
  const uiRights = yield select(selectUiRights);
  if (!uiRights || typeof uiRights !== 'object') {
    return;
  }
  const { administration_stripe_customer_portal_ui } = uiRights;
  /* 
  The getAllOrganizations call returns an array of all existing organizations.
  If the user doesn't have the rights to see all organizations then the call returns only his organization. 
  */
  yield all([put(getOrganizations()), put(getInsuredRisks()), put(getFileContainers()), put(getWorklistsItemCount())]);
  // If the user has the right to see the customer portal then we check the customer portal availability
  if (administration_stripe_customer_portal_ui) {
    yield put(getCustomerPortalAvailability());
  }
}

export default function* authenticationSaga() {
  yield all([
    takeLatest(loginWithPassword.type, loginWithPasswordWorker),
    takeLatest(loginWithMicrosoft.type, loginWithMicrosoftWorker),
    takeLatest(loginWithPasswordAndApproveTermsOfUse.type, loginWithPasswordAndApproveTermsOfUseWorker),
    takeLatest(loginWithMicrosoftAndApproveTermsOfUse.type, loginWithMicrosoftAndApproveTermsOfUseWorker),
    takeLatest(refreshMicrosoftSession.type, refreshMicrosoftSessionWorker),
    takeLatest(validateUser.type, validateUserWorker),
    takeLatest(logout.type, logoutWorker),
    takeLatest(getAllowedSessionTypes.type, getAllowedSessionTypesWorker),
    takeLatest(getUiRights.type, getUiRightsWorker),
    takeLatest(approveTermsOfUse.type, approveTermsOfUseWorker),
    takeLatest(getUiRightsSucceeded.type, loadEnvironmentSpecificDataWorker)
  ]);
}
